import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Phone, MessageCircle, Check } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Home = () => {
  const { toast } = useToast();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const phoneNumber = '07999166424';
  const whatsappNumber = '07999166424';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Send form data to backend
      const response = await axios.post(`${API}/contact`, formData);
      console.log('Form submitted successfully:', response.data);
      
      toast({
        title: "Message Sent!",
        description: "We'll get back to you within 24 hours."
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again or call us directly.",
        variant: "destructive",
      });
    }
  };

  const handleCallClick = () => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/44${whatsappNumber.substring(1)}`, '_blank');
  };

  const handleSignUp = async (plan) => {
    setIsProcessingPayment(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payment/create-checkout`, {
        plan: plan,
        origin_url: originUrl
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast({
        title: "Error",
        description: "Unable to start payment. Please try again or contact us.",
        variant: "destructive",
      });
      setIsProcessingPayment(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Banner Header */}
      <header className="bg-[#1e3a5f] text-white sticky top-0 z-50 shadow-lg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_d704a92b-a1bb-4e23-a384-770d0b9f3256/artifacts/rb248az1_image.png" 
                alt="Local Biz Websites" 
                className="h-16 w-auto"
              />
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">Local Biz Websites</h1>
                <p className="text-gray-200 text-sm">Helping Local Businesses Grow Online</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button 
                onClick={handleCallClick}
                variant="outline" 
                size="lg"
                className="bg-white text-[#1e3a5f] hover:bg-gray-100 border-0 font-semibold"
              >
                <Phone className="h-4 w-4 mr-2" />
                Call Us
              </Button>
              <Button 
                onClick={handleWhatsAppClick}
                size="lg"
                className="bg-[#f97316] hover:bg-[#ea580c] text-white font-semibold"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            Simple Websites for Local Service Businesses
          </h2>
          <p className="text-xl sm:text-2xl text-gray-600 mb-10 max-w-3xl mx-auto">
            We build and manage websites that make it easy for customers to call you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-6">

              Get a Free Website Demo
            </Button>
            <Button
              onClick={handleCallClick}
              size="lg"
              variant="outline"
              className="text-lg px-8 py-6 border-2 border-gray-300">

              Call or WhatsApp
            </Button>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 text-center">
            What We Do
          </h3>
          <p className="text-lg sm:text-xl text-gray-700 leading-relaxed text-center max-w-3xl mx-auto">
            We build mobile-friendly websites designed to get your phone ringing. 
            Your website will be clear, professional, and focused on what matters most: making it easy for customers to contact you. 
            Once it's live, we handle all the ongoing maintenance and updates, so you never have to think about it.
          </p>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
            How It Works
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">We Build Your Website First</h4>
              <p className="text-gray-600 text-base leading-relaxed">
                We create your website upfront, at no cost to you. No payment until you see it and like it.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-blue-600">2</span>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">You Review It</h4>
              <p className="text-gray-600 text-base leading-relaxed">
                Take your time to look it over. Make sure it's exactly what you want. No pressure, no rush.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-blue-600">3</span>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">We Manage It Monthly</h4>
              <p className="text-gray-600 text-base leading-relaxed">
                If you're happy, we'll keep it running smoothly with ongoing updates and support.
              </p>
            </div>
          </div>
          <p className="text-center text-lg text-gray-600 mt-10">
            No upfront cost. No pressure. Cancel anytime.
          </p>
        </div>
      </section>

      {/* Example Websites Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
            Example Websites
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Plumber Website Mockup */}
            <Card className="border-2 hover:border-blue-300 transition-all duration-300 hover:shadow-lg overflow-hidden group">
              <CardHeader className="p-0">
                <div className="w-full h-64 bg-white rounded-t-md overflow-hidden border-b-2 border-gray-200 relative">
                  {/* Mini Website Mockup - Plumber */}
                  <div className="scale-50 origin-top-left w-[200%] h-[200%] transform">
                    <div className="bg-blue-600 text-white px-8 py-4 flex justify-between items-center">
                      <div className="font-bold text-2xl">QuickFix Plumbing</div>
                      <div className="bg-white text-blue-600 px-6 py-2 rounded font-bold">
                        Call Now
                      </div>
                    </div>
                    <div className="bg-gradient-to-b from-blue-50 to-white px-8 py-12 text-center">
                      <h1 className="text-5xl font-bold text-gray-900 mb-4">
                        24/7 Emergency Plumber
                      </h1>
                      <p className="text-2xl text-gray-600 mb-6">
                        Basildon's Trusted Local Plumber
                      </p>
                      <button className="bg-blue-600 text-white px-12 py-4 rounded-lg text-2xl font-bold">
                        📞 07123 456789
                      </button>
                    </div>
                    <div className="bg-white px-8 py-8 grid grid-cols-3 gap-4 border-t">
                      <div className="text-center">
                        <div className="text-3xl mb-2">🚿</div>
                        <div className="text-lg font-semibold">Leaks</div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl mb-2">🔧</div>
                        <div className="text-lg font-semibold">Repairs</div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl mb-2">⚡</div>
                        <div className="text-lg font-semibold">Fast</div>
                      </div>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-blue-600 bg-opacity-0 group-hover:bg-opacity-5 transition-all duration-300"></div>
                </div>
                <div className="p-4">
                  <CardTitle className="text-xl mb-2">Emergency Plumbing Services</CardTitle>
                  <CardDescription className="text-base">
                    Clean layout with prominent call button and service areas clearly listed.
                  </CardDescription>
                </div>
              </CardHeader>
            </Card>

            {/* Electrician Website Mockup */}
            <Card className="border-2 hover:border-blue-300 transition-all duration-300 hover:shadow-lg overflow-hidden group">
              <CardHeader className="p-0">
                <div className="w-full h-64 bg-white rounded-t-md overflow-hidden border-b-2 border-gray-200 relative">
                  {/* Mini Website Mockup - Electrician */}
                  <div className="scale-50 origin-top-left w-[200%] h-[200%] transform">
                    <div className="bg-yellow-500 text-gray-900 px-8 py-4 flex justify-between items-center">
                      <div className="font-bold text-2xl">⚡ BrightSpark Electric</div>
                      <div className="bg-gray-900 text-yellow-500 px-6 py-2 rounded font-bold">
                        Get Quote
                      </div>
                    </div>
                    <div className="bg-gradient-to-b from-yellow-50 to-white px-8 py-12 text-center">
                      <h1 className="text-5xl font-bold text-gray-900 mb-4">
                        Qualified Local Electrician
                      </h1>
                      <p className="text-2xl text-gray-600 mb-6">
                        Serving Essex Since 2010
                      </p>
                      <button className="bg-yellow-500 text-gray-900 px-12 py-4 rounded-lg text-2xl font-bold">
                        📞 Call For Free Quote
                      </button>
                    </div>
                    <div className="bg-gray-50 px-8 py-8">
                      <div className="bg-white p-6 rounded-lg shadow-sm">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="text-3xl">✓</div>
                          <div className="text-xl">Fully Certified & Insured</div>
                        </div>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="text-3xl">✓</div>
                          <div className="text-xl">Same Day Service</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-3xl">✓</div>
                          <div className="text-xl">All Work Guaranteed</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-yellow-500 bg-opacity-0 group-hover:bg-opacity-5 transition-all duration-300"></div>
                </div>
                <div className="p-4">
                  <CardTitle className="text-xl mb-2">Local Electrician</CardTitle>
                  <CardDescription className="text-base">
                    Mobile-friendly design focused on getting customers to call for quotes.
                  </CardDescription>
                </div>
              </CardHeader>
            </Card>

            {/* Heating Website Mockup */}
            <Card className="border-2 hover:border-blue-300 transition-all duration-300 hover:shadow-lg overflow-hidden group">
              <CardHeader className="p-0">
                <div className="w-full h-64 bg-white rounded-t-md overflow-hidden border-b-2 border-gray-200 relative">
                  {/* Mini Website Mockup - Heating */}
                  <div className="scale-50 origin-top-left w-[200%] h-[200%] transform">
                    <div className="bg-orange-600 text-white px-8 py-4 flex justify-between items-center">
                      <div className="font-bold text-2xl">🔥 WarmHome Heating</div>
                      <div className="bg-white text-orange-600 px-6 py-2 rounded font-bold">
                        Book Now
                      </div>
                    </div>
                    <div className="bg-gradient-to-b from-orange-50 to-white px-8 py-12 text-center">
                      <h1 className="text-5xl font-bold text-gray-900 mb-4">
                        Boiler Repairs & Servicing
                      </h1>
                      <p className="text-2xl text-gray-600 mb-6">
                        Keep Your Home Warm All Year
                      </p>
                      <button className="bg-orange-600 text-white px-12 py-4 rounded-lg text-2xl font-bold">
                        📞 Emergency: 07123 456789
                      </button>
                    </div>
                    <div className="bg-white px-8 py-8 grid grid-cols-2 gap-6">
                      <div className="bg-orange-50 p-6 rounded-lg text-center">
                        <div className="text-4xl font-bold text-orange-600 mb-2">£59</div>
                        <div className="text-lg">Boiler Service</div>
                      </div>
                      <div className="bg-orange-50 p-6 rounded-lg text-center">
                        <div className="text-4xl font-bold text-orange-600 mb-2">24/7</div>
                        <div className="text-lg">Emergency Callout</div>
                      </div>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-orange-600 bg-opacity-0 group-hover:bg-opacity-5 transition-all duration-300"></div>
                </div>
                <div className="p-4">
                  <CardTitle className="text-xl mb-2">Boiler Repairs & Heating</CardTitle>
                  <CardDescription className="text-base">
                    Simple, professional site that builds trust and makes booking easy.
                  </CardDescription>
                </div>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 text-center">
            Simple, Transparent Pricing
          </h3>
          <p className="text-center text-lg text-gray-600 mb-12">Cancel anytime. No contracts.</p>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-2xl">Starter</CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-gray-900">£29</span>
                  <span className="text-gray-600 text-lg">/month</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Hosting included</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Website built & kept live</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Mobile-friendly design</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Contact form</span>
                  </li>
                </ul>
                <Button
                  onClick={() => handleSignUp('starter')}
                  disabled={isProcessingPayment}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11"
                >
                  {isProcessingPayment ? 'Processing...' : 'Sign Up Now'}
                </Button>
              </CardContent>
            </Card>
            <Card className="border-2 border-blue-600">
              <CardHeader>
                <CardTitle className="text-2xl">Growth</CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-gray-900">£59</span>
                  <span className="text-gray-600 text-lg">/month</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Everything in Starter</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Small content updates</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Google visibility setup</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Priority support</span>
                  </li>
                </ul>
                <Button
                  onClick={() => handleSignUp('growth')}
                  disabled={isProcessingPayment}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11"
                >
                  {isProcessingPayment ? 'Processing...' : 'Sign Up Now'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            About Us
          </h3>
          <p className="sm:text-xl max-w-3xl !text-lg mx-auto text-gray-700">We work with local service businesses in Basildon and across Essex to make getting online simple and stress-free. We build clean, mobile-friendly websites that make it easy for customers to call you, and we handle the technical side so you don’t have to worry about it. That way, you can focus on running your business while we look after your website.



          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 text-center">
            Get In Touch
          </h3>
          <p className="text-center text-lg text-gray-600 mb-10">
            Ready to get started? Send us a message or give us a call.
          </p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Form */}
            <div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-base">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="mt-1 text-base h-11"
                    placeholder="Your name" />

                </div>
                <div>
                  <Label htmlFor="email" className="text-base">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="mt-1 text-base h-11"
                    placeholder="your.email@example.com" />

                </div>
                <div>
                  <Label htmlFor="phone" className="text-base">Phone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="mt-1 text-base h-11"
                    placeholder="07123 456789" />

                </div>
                <div>
                  <Label htmlFor="message" className="text-base">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="mt-1 text-base"
                    placeholder="Tell us about your business..." />

                </div>
                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white text-base h-11">

                  Send Message
                </Button>
              </form>
            </div>

            {/* Contact Options */}
            <div className="flex flex-col justify-center space-y-6">
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-4">Prefer to talk?</h4>
                <p className="text-gray-600 mb-6 text-base">
                  Give us a call or send a WhatsApp message. We're here to help.
                </p>
              </div>
              <Button
                onClick={handleCallClick}
                variant="outline"
                size="lg"
                className="w-full justify-start text-base h-14 border-2">

                <Phone className="h-5 w-5 mr-3" />
                Call {phoneNumber}
              </Button>
              <Button
                onClick={handleWhatsAppClick}
                size="lg"
                className="w-full justify-start bg-green-600 hover:bg-green-700 text-white text-base h-14">

                <MessageCircle className="h-5 w-5 mr-3" />
                WhatsApp {whatsappNumber}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-lg font-semibold mb-2">Local Biz Websites</p>
          <p className="text-gray-400 text-sm">
            Simple websites for local service businesses.
          </p>
          <p className="text-gray-400 text-sm mt-4">
            © {new Date().getFullYear()} Local Biz Websites. All rights reserved.
          </p>
        </div>
      </footer>
    </div>);

};

export default Home;